const API_BASE_URL = 'http://localhost:8000/api/v1';

export interface UserApp {
  id: string;
  userId: string;
  appKey: string;
  grantedAt: string;
  grantedById?: string;
}

export interface UserAppPermissionsResponse {
  success: boolean;
  apps: string[];
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.statusText}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// Get user's app permissions
export async function fetchUserApps(userId: string): Promise<string[]> {
  const response: UserAppPermissionsResponse = await fetchWithAuth(`/user-apps/users/${userId}/apps`);
  return response.apps || [];
}

// Grant app access to a user
export async function grantAppAccess(userId: string, appKey: string): Promise<void> {
  await fetchWithAuth(`/user-apps/users/${userId}/apps`, {
    method: 'POST',
    body: JSON.stringify({ appKey }),
  });
}

// Batch grant apps
export async function batchGrantAppAccess(userId: string, appKeys: string[]): Promise<void> {
  await fetchWithAuth(`/user-apps/users/${userId}/apps/batch`, {
    method: 'POST',
    body: JSON.stringify({ appKeys }),
  });
}

// Grant all apps
export async function grantAllApps(userId: string): Promise<void> {
  await fetchWithAuth(`/user-apps/users/${userId}/apps/grant-all`, {
    method: 'POST',
  });
}

// Revoke app access
export async function revokeAppAccess(userId: string, appKey: string): Promise<void> {
  await fetchWithAuth(`/user-apps/users/${userId}/apps/${appKey}`, {
    method: 'DELETE',
  });
}

// Get users by app
export async function getUsersByApp(appKey: string): Promise<any[]> {
  const response = await fetchWithAuth(`/user-apps/apps/${appKey}/users`);
  return response.users || [];
}
